import { i18n } from '../i18n.js';
import { CardTilt } from './card-tilt.js';
import {
  buildInviteTicket,
  relocalizeInviteTicket,
  warmInviteTicketArt,
  whenInviteTicketWhole,
} from './invite-ticket-object.js';
import { CopyText } from './copy-text.js';
import { BootVeil } from './boot-veil.js';
import { MoonfallAnalytics } from '../analytics/posthog-init.js';
import { MoonfallCloud } from '../convex-client.js';
import { MoonfallClickGuard } from '../utils/click-guard.js';
/**
 * Lobby referral program. Convex owns eligibility, caps, expiry, progress, and
 * rewards; this module renders those server terms and provides native share /
 * clipboard affordances without exposing either player's identity.
 */

import { Lobby } from './lobby.js';
import { Platform } from './platform.js';
import { Stardust } from './stardust.js';
import { paintStardustChip } from './stardust-chip.js';
import { StarterReward } from './starter-reward.js';
let Referrals = {};

Referrals = (function () {
  let initialized = false;
  let program = null;
  let serverTerms = null;
  let invitedBy = null;
  let dialog = null;
  let previousFocus = null;
  let appWasInert = false;
  let consumeInFlight = null;
  let openInFlight = null;
  let pendingOpen = null;
  let refreshInFlight = null;
  let statusLoaded = false;
  let openGeneration = 0;
  const SURFACE_OWNER = 'referrals';
  const OPEN_TIMEOUT_MS = 10_000;
  /* THE DIALOG OPENS ON A FINISHED TICKET, OR NOT YET.

     Reported on this exact screen: "when clicked, especially from an uncached
     source, it first loads the text and then it is loading the image, so the
     image can come after one second -- it breaks the illusion of a physical
     object and it also looks buggy". Measured with the artwork throttled: the
     dialog was fully on screen at 330ms with MOONFALL, the date, ADMIT ONE and
     the serial printed onto an empty well, and the 326KB artwork arrived
     underneath them at 1.6s. The arrival screen already held itself for this;
     the dialog did not.

     So the press is acknowledged on the button (pending), the artwork is
     warmed on intent and again at the click, and the dialog is appended only
     once the ticket's own <img> has decoded -- one entrance, one whole object.
     The wait is capped: past it the dialog opens as it stands, because a
     stalled or missing image may never trap the player behind a busy button.
     4s is the same cap the cardback cohort uses for a static-art object dealt
     in whole; with the warm ahead of it, an ordinary connection never reaches
     it and a warm cache pays a frame. */
  const TICKET_WHOLE_CAP_MS = 4000;

  function claimLobbySurface() {
    const body = document.body;
    if (!body
      || body.classList.contains('game-transition-pending')
      || body.classList.contains('game-active')) return false;
    const owner = body.dataset.lobbySurfaceOwner;
    if (owner && owner !== SURFACE_OWNER) return false;
    body.dataset.lobbySurfaceOwner = SURFACE_OWNER;
    return true;
  }

  function releaseLobbySurface() {
    if (document.body?.dataset?.lobbySurfaceOwner === SURFACE_OWNER) {
      delete document.body.dataset.lobbySurfaceOwner;
    }
  }

  function openSessionIsCurrent(generation) {
    return generation === openGeneration
      && document.body?.dataset?.lobbySurfaceOwner === SURFACE_OWNER
      && !document.body.classList.contains('game-transition-pending')
      && !document.body.classList.contains('game-active');
  }

  function createOpenSession(remoteRequest) {
    let settleCancellation;
    const cancellation = new Promise(resolve => { settleCancellation = resolve; });
    const timeout = setTimeout(() => {
      settleCancellation({ kind: 'timeout' });
    }, OPEN_TIMEOUT_MS);
    timeout.unref?.();

    const remoteOutcome = Promise.resolve(remoteRequest).then(
      value => ({ kind: 'loaded', value }),
      error => ({ kind: 'failed', error }),
    );

    return {
      outcome: Promise.race([remoteOutcome, cancellation])
        .finally(() => clearTimeout(timeout)),
      /* Settles on cancel or timeout, so a later wait (the ticket decode) can
         be raced against it and let go the moment the session is closed. */
      cancelled: cancellation,
      cancel() {
        settleCancellation({ kind: 'cancelled' });
      },
    };
  }

  function uiText(key, fallback, replacements = {}) {
    const translated = i18n?.t?.(key, replacements);
    const template = translated && translated !== key ? translated : fallback;
    return Object.entries(replacements).reduce(
      (text, [name, value]) => String(text).replaceAll(`{${name}}`, String(value)),
      String(template),
    );
  }

  const formatStardust = (value) => Stardust?.format?.(value) ?? String(value);

  function analytics(event, properties) {
    if (properties === undefined) MoonfallAnalytics?.capture?.(event);
    else MoonfallAnalytics?.capture?.(event, properties);
  }

  function make(tag, { className, id, text, type } = {}) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (id) element.id = id;
    if (text !== undefined) element.textContent = text;
    if (type) element.type = type;
    return element;
  }

  function rewardTerms(data = program || serverTerms || {}) {
    return {
      /* The reward is card packs now, not Stardust. Packs cannot leave the
         account, which is what let the Moon Exchange lock go. */
      packs: Number.isSafeInteger(data.rewardPacks) ? data.rewardPacks : 3,
      matches: Number.isSafeInteger(data.qualifyingMatches) ? data.qualifyingMatches : 3,
      cap: Number.isSafeInteger(data.lifetimeCap) ? data.lifetimeCap : 3,
      days: Number.isSafeInteger(data.activationWindowDays) ? data.activationWindowDays : 30,
    };
  }

  function displayRewardTerms(terms, packs = terms.packs) {
    return { ...terms, packs };
  }

  /** A share sheet is offered only where the device is touch-first AND the
      engine implements navigator.share — the room invite's rule (lobby.js). */
  function offersShareSheet() {
    if (typeof navigator.share !== 'function') return false;
    /** @type {any} */
    const platform = Platform;
    return Boolean(platform?.isMobileUA?.() || platform?.isTouchPrimary?.());
  }

  function inviteUrl(code) {
    const url = new URL('/play', window.location.origin);
    url.searchParams.set('ref', code);
    url.searchParams.set('utm_source', 'player_referral');
    url.searchParams.set('utm_medium', 'referral');
    return url.toString();
  }

  /* HAS THIS PLAYER SPENT EVERY INVITE?
     The lobby rail is small and this banner is a call to invite, so once the
     rewards are all earned it advertises something the player cannot do while
     occupying space beside the controls they actually use.

     The cap comes from the SERVER's terms. `rewardTerms()` falls back to 3 for
     a program that has not loaded yet, which is exactly when we must not
     retire anything — hence the `program` guard: an unknown state is treated
     as "invites remain", so the banner is never hidden on a slow load.

     `invitedBy` keeps it alive regardless. The same banner carries the
     player's OWN invitation progress when somebody invited them, and retiring
     it for a spent inviter must not also delete the reward they are still
     owed as an invitee. */
  function invitesExhausted() {
    if (!program) return false;
    if (invitedBy?.status === 'pending') return false;
    const { cap } = rewardTerms();
    if (!Number.isSafeInteger(cap) || cap <= 0) return false;
    return (program.qualifiedCount || 0) >= cap;
  }

  function renderBannerStatus() {
    if (invitesExhausted()) {
      /* Remove rather than hide: a display:none banner still occupies the
         utility rail's grid and leaves a gap where it used to be. */
      document.getElementById('referral-banner')?.remove();
      return;
    }
    const status = document.getElementById('referral-banner-status');
    const copy = document.getElementById('referral-banner-copy');
    const terms = rewardTerms();
    if (copy) {
      copy.textContent = uiText(
        'referral.bannerCopy',
        'You both get {packs} card packs and Moonlight after they finish {matches} eligible completed matches.',
        displayRewardTerms(terms),
      );
    }
    if (!status) return;
    if (invitedBy?.status === 'pending') {
      status.textContent = uiText(
        'referral.inviteeProgress',
        'Your invitation: {completed}/{matches} matches · {packs} packs waiting',
        {
          completed: invitedBy.completedMatches || 0,
          matches: invitedBy.qualifyingMatches || terms.matches,
          packs: invitedBy.rewardPacks || terms.packs,
        },
      );
      return;
    }
    if (invitedBy?.status === 'qualified') {
      status.textContent = uiText(
        'referral.inviteeQualified',
        'Invitation complete · {packs} packs earned',
        { packs: invitedBy.rewardPacks || terms.packs },
      );
      return;
    }
    status.textContent = program
      ? uiText(
        'referral.bannerProgress',
        '{qualified}/{cap} rewards earned · {active} in progress',
        {
          qualified: program.qualifiedCount || 0,
          cap: terms.cap,
          active: program.activeCount || 0,
        },
      )
      : uiText('referral.bannerReady', 'Share an invitation link');
  }

  function syncBannerPlacement() {
    const banner = document.getElementById('referral-banner');
    if (!banner) return;
    /* The hub gives the invitation ONE home on every layout: the Community
       sheet, under Discord (hub.css). The phone top-bar pill and the desktop
       utility rail below are kept only for a page that has no hub. */
    const communityBody = document.getElementById('hub-community-body');
    if (communityBody) {
      if (banner.parentElement !== communityBody) communityBody.appendChild(banner);
      return;
    }
    const commandCenter = document.querySelector('#lobby-view .lobby-command-center');
    const utilityRail = document.getElementById('lobby-utility-rail');
    const userControls = document.getElementById('user-controls');
    /* Match the viewport media queries that own the compact 44px treatment.
       `isMobileUi()` is deliberately NOT used: it carries a UA-only fallback
       that would relocate the banner on a tablet reporting a desktop viewport,
       where no compact CSS applies, and drop the full desktop rail into the top
       bar. Both predicates below have a matching media block — the phone gate
       in mobile.css and the landscape-tablet block beneath it — so the pill
       styling always travels with the move. */
    /* `platform.js` exports `Platform` as a bare object literal, so `checkJs`
       types every method access on it as a property of `{}`. One annotated
       local answers for both calls instead of adding a second ratchet error
       (`npm run typecheck:client`). */
    /** @type {any} */
    const platform = Platform;
    const compact = platform?.isMobileViewport?.() === true
      || platform?.isTabletLandscapeViewport?.() === true;

    if (compact && userControls) {
      if (banner.parentElement !== userControls) userControls.appendChild(banner);
      // The top bar owns its own left-to-right order (Feedback, account pills,
      // Invite, Discord). Appending here would otherwise leave Discord stranded
      // to the LEFT of the Invite pill it is meant to follow.
      Lobby?.syncUtilityPlacement?.();
      return;
    }
    if (utilityRail) {
      if (banner.parentElement !== utilityRail) utilityRail.appendChild(banner);
      return;
    }
    if (commandCenter && banner.previousElementSibling !== commandCenter) {
      commandCenter.insertAdjacentElement('afterend', banner);
    }
  }

  function createBanner() {
    const commandCenter = document.querySelector('#lobby-view .lobby-command-center');
    if (!commandCenter || document.getElementById('referral-banner')) return;
    /* Already known to be spent — build nothing, so returning to the lobby
       does not flash the banner before `renderBannerStatus` removes it. */
    if (invitesExhausted()) return;
    const lobbyScroll = commandCenter.closest('.lobby-scroll');
    let utilityRail = document.getElementById('lobby-utility-rail');
    if (!utilityRail && lobbyScroll && !document.getElementById('hub-community-body')) {
      utilityRail = make('div', { className: 'lobby-utility-rail', id: 'lobby-utility-rail' });
      commandCenter.insertAdjacentElement('afterend', utilityRail);
    }

    const banner = make('aside', { className: 'referral-banner', id: 'referral-banner' });
    banner.setAttribute('aria-labelledby', 'referral-banner-title');
    const mark = make('span', { className: 'referral-banner-mark', text: '✦' });
    mark.setAttribute('aria-hidden', 'true');
    const content = make('div', { className: 'referral-banner-content' });
    const title = make('h2', {
      id: 'referral-banner-title',
      text: uiText('referral.bannerTitle', 'Invite a Mooncaller'),
    });
    title.setAttribute('data-i18n', 'referral.bannerTitle');
    const copy = make('p', { id: 'referral-banner-copy' });
    const status = make('p', { className: 'referral-banner-status', id: 'referral-banner-status' });
    content.append(title, copy, status);
    const openButton = make('button', {
      className: 'referral-banner-open',
      id: 'referral-banner-open',
      type: 'button',
    });
    const openIcon = make('span', { className: 'referral-open-icon', text: '✦' });
    openIcon.setAttribute('aria-hidden', 'true');
    const openFullLabel = make('span', {
      className: 'referral-open-full-label',
      text: uiText('referral.open', 'Invite & earn'),
    });
    openFullLabel.setAttribute('data-i18n', 'referral.open');
    const openCompactLabel = make('span', {
      className: 'referral-open-compact-label',
      text: uiText('referral.mobileOpen', 'Invite'),
    });
    openCompactLabel.setAttribute('data-i18n', 'referral.mobileOpen');
    openButton.append(openIcon, openFullLabel, openCompactLabel);
    openButton.setAttribute('aria-label', uiText('referral.open', 'Invite & earn'));
    openButton.setAttribute('data-i18n-aria-label', 'referral.open');
    /* Hover, focus and the first touch are the intent to open, and each is a
       head start on the artwork (docs/asset-loading.md law 3): warmed here, the
       decode the dialog waits on below is normally already satisfied by the
       time the code round-trip returns. The warm is memoized, so a player who
       hovers ten times costs one request. */
    const warmTicket = () => { warmInviteTicketArt(); };
    openButton.addEventListener('pointerenter', warmTicket, { passive: true });
    openButton.addEventListener('focus', warmTicket);
    openButton.addEventListener('touchstart', warmTicket, { passive: true });
    /* The click guard holds the button busy (aria-busy, disabled, the pending
       visual) for exactly as long as `open` is in flight -- the code round-trip
       plus the ticket decode -- so a press on a cold cache is never a press
       that did nothing. Left unguarded, this button used to go silent for the
       whole round-trip anyway; the hold only made that silence worth fixing. */
    openButton.addEventListener('click', () => {
      const run = () => open({ returnFocus: openButton });
      const guard = MoonfallClickGuard?.guard;
      const request = typeof guard === 'function' ? guard(openButton, run) : run();
      Promise.resolve(request).catch(() => { /* open() reports its own failures */ });
    });
    banner.append(mark, content, openButton);
    (utilityRail || commandCenter).appendChild(banner);
    syncBannerPlacement();
    renderBannerStatus();
    /* And once the lobby is quiet, ahead of any intent. A phone has no hover
       and its first touch is ~100ms before the click, which is not a head
       start on 326KB; warmed at idle priority while the player reads the
       lobby, the cold press above becomes the rare case instead of the phone
       case. Only a player who can still invite -- the banner's own condition
       -- pays for it, and never ahead of anything the lobby itself needs. */
    const idle = typeof window.requestIdleCallback === 'function'
      ? (fn) => window.requestIdleCallback(fn, { timeout: 4000 })
      : (fn) => setTimeout(fn, 1500);
    /* Not while the boot veil is up: the lobby's own reads own the link until
       it lifts (ui/lobby-arrival.js holds the veil for them). */
    void BootVeil.whenReleased().then(() => idle(() => {
      /* A player who asked for Data Saver did not ask for a speculative
         326KB; intent and the press itself still warm it for them. */
      let saveData = false;
      try {
        /* Network Information API: Chromium and Android only, hence untyped. */
        saveData = /** @type {any} */ (navigator).connection?.saveData === true;
      } catch (_) { saveData = false; }
      if (saveData) return;
      warmInviteTicketArt(undefined, { priority: 'low' });
    }));
  }

  async function refresh() {
    if (refreshInFlight) return refreshInFlight;
    const getProgram = MoonfallCloud?.referrals?.getProgram;
    if (typeof getProgram !== 'function') return null;
    refreshInFlight = (async () => {
      try {
        const result = await getProgram.call(MoonfallCloud.referrals);
        program = result?.program || null;
        serverTerms = result?.terms || serverTerms;
        invitedBy = result?.invitedBy || null;
        statusLoaded = true;
        renderBannerStatus();
        return result;
      } catch (error) {
        console.warn('[Referrals] Failed to load referral status:', error?.message || error);
        return null;
      } finally {
        refreshInFlight = null;
      }
    })();
    return refreshInFlight;
  }

  function showToast(text) {
    document.querySelector('.referral-toast')?.remove();
    const toast = make('div', { className: 'referral-toast', text });
    toast.setAttribute('role', 'status');
    document.body.appendChild(toast);
    const exit = setTimeout(() => {
      toast.classList.add('referral-toast--exit');
      setTimeout(() => toast.remove(), 240);
    }, 2400);
    exit.unref?.();
  }

  /* Shared ladder: the local fallback here had the same iOS-hostile shape as
     the lobby's (readonly textarea + bare .select()). The button beside this
     one already offers the share sheet, so the sheet rung is disabled — a
     failed copy must not surprise-open it. */
  async function copyText(value) {
    const outcome = await CopyText.copy(value, { allowShareFallback: false });
    if (!outcome.ok) throw new Error('clipboard_unavailable');
  }

  function close({ restoreFocus = true } = {}) {
    const wasOpen = Boolean(dialog || openInFlight);
    const session = pendingOpen;
    pendingOpen = null;
    session?.cancel();
    openGeneration += 1;
    openInFlight = null;
    if (dialog) {
      document.removeEventListener('keydown', onDialogKeyDown, true);
      dialog.remove();
      dialog = null;
      const app = document.getElementById('app');
      if (app) app.inert = appWasInert;
      appWasInert = false;
    }
    releaseLobbySurface();
    if (restoreFocus
      && !document.body?.classList.contains('game-transition-pending')
      && !document.body?.classList.contains('game-active')
      && previousFocus?.isConnected) previousFocus.focus();
    previousFocus = null;
    return wasOpen;
  }

  function onDialogKeyDown(event) {
    if (!dialog) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const controls = [...dialog.querySelectorAll('button:not([disabled]), input:not([disabled])')];
    if (!controls.length) return;
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /** Whose ticket this is. The sender IS the issuer. */
  function inviterName() {
    try {
      const el = document.getElementById('user-username');
      const name = String(el?.textContent || '').trim();
      return name && name.length <= 40 ? name : null;
    } catch (_) {
      return null;
    }
  }

  function buildProgramDialog(data) {
    const terms = rewardTerms(data);
    const link = inviteUrl(data.code);
    const overlay = make('div', { className: 'referral-dialog', id: 'referral-dialog' });
    const card = make('section', { className: 'referral-dialog-card' });
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-labelledby', 'referral-dialog-title');
    card.setAttribute('aria-describedby', 'referral-dialog-terms');

    const closeButton = make('button', {
      className: 'referral-dialog-close',
      id: 'referral-dialog-close',
      text: '×',
      type: 'button',
    });
    closeButton.setAttribute('aria-label', uiText('referral.close', 'Close invitations'));
    closeButton.addEventListener('click', close);

    const eyebrow = make('p', {
      className: 'referral-dialog-eyebrow',
      text: uiText('referral.eyebrow', 'MOONFALL REFERRALS'),
    });
    const title = make('h2', {
      id: 'referral-dialog-title',
      text: uiText('referral.title', 'Give {packs} packs. Get {packs} packs.', displayRewardTerms(terms)),
    });
    /*
     * A COUNTER, NOT A SENTENCE.
     *
     * This read "{qualified} of {cap} referral rewards earned · {active} in
     * progress" -- a line nobody parses, and "in progress" describes an
     * internal state rather than anything the player can act on. The number is
     * the whole message, so the number gets the size.
     */
    const stat = (value, caption, id) => {
      /* The id AND the matching class: both were load-bearing selectors before
         these became stats, in the unit suite and the browser suite. */
      const wrap = make('div', { className: id ? `referral-stat ${id}` : 'referral-stat' });
      if (id) wrap.id = id;
      wrap.append(
        make('strong', { className: 'referral-stat-value', text: value }),
        make('span', { className: 'referral-stat-label', text: caption }),
      );
      return wrap;
    };

    const stats = make('div', { className: 'referral-dialog-stats' });
    stats.appendChild(stat(
      `${data.qualifiedCount || 0}/${terms.cap}`,
      uiText('referral.statInvites', 'Invitations earned'),
      'referral-program-progress',
    ));
    if (invitedBy) {
      stats.appendChild(invitedBy.status === 'qualified'
        ? stat(
          '\u2713',
          uiText('referral.statYoursDone', 'Your invitation'),
          'referral-invitee-progress',
        )
        : stat(
          `${invitedBy.completedMatches || 0}/${invitedBy.qualifyingMatches || terms.matches}`,
          uiText('referral.statYours', 'Your invitation'),
          'referral-invitee-progress',
        ));
    }

    /*
     * THE OBJECT, not a form field.
     *
     * The link itself was printed into a read-only input with its own label --
     * a URL nobody reads, taking the most valuable space on the screen. What
     * the player is actually sending is a TICKET, so that is what the screen
     * shows; the link leaves via the button.
     */
    const object = make('div', { className: 'referral-dialog-object' });
    /*
     * THE SAME OBJECT THE RECEIVER GETS, not a picture of it.
     *
     * This was a bare <img> of the artwork with none of the printing on it, so
     * the ticket the sender looked at and the ticket that arrives on the other
     * end were visibly different things. It is built by the shared builder now
     * — wordmark, issue date, issued-by line, admission stamp and serial — and
     * registered with the same tilt engine, so it rotates and catches the light
     * exactly as it does on the arrival screen.
     */
    const { ticket, art: ticketArt } = buildInviteTicket({ code: data.code, issuedBy: inviterName() });
    object.appendChild(ticket);
    try { CardTilt?.registerPreview?.(ticket); } catch (_) { /* tilt is decoration */ }

    const actions = make('div', { className: 'referral-dialog-actions' });
    /* ONE action, not two. A touch-first device with a share sheet gets the
       sheet: the link travels as `url`, so Messages, WhatsApp and Discord draw
       the invitation card from the link's Open Graph tags exactly as the
       copied link unfurls (server.js resolves the card image for `?ref=`
       links). A refused sheet — WebKit's "share() is already in progress"
       after a sheet torn down by an app switch, a host that withheld the
       policy — copies the link through the same ladder and SAYS so; a
       dismissed sheet says nothing. A desktop keeps Copy link even where
       share exists (Safari on a Mac): there the sheet is a detour from a chat
       window that already has the clipboard. Same rule as the room invite
       (lobby.js `offersInviteShareSheet`), asked for by the owner: "replace
       the copy invite button with an invite button that natively handles
       sharing and keeps thumbnails". */
    let primaryAction;
    if (offersShareSheet()) {
      const shareButton = make('button', {
        className: 'referral-share',
        id: 'referral-share',
        text: uiText('referral.share', 'Share invitation'),
        type: 'button',
      });
      shareButton.addEventListener('click', () => {
        /* `navigator.share` is called synchronously from the tap: both
           platforms tie it to the gesture's transient activation, and an
           `await` before it would lose the gesture on WebKit. */
        let sheet;
        try {
          sheet = navigator.share({
            title: uiText('referral.shareTitle', 'Join me in Moonfall'),
            text: uiText(
              'referral.shareText',
              'Enter Moonfall with me. We each get {packs} card packs after {matches} completed matches.',
              displayRewardTerms(terms),
            ),
            url: link,
          });
        } catch (error) {
          sheet = Promise.reject(error);
        }
        Promise.resolve(sheet).then(
          () => { analytics('referral_share_completed', { method: 'native' }); },
          async (error) => {
            const cancelled = error?.name === 'AbortError';
            analytics('referral_share_failed', { method: 'native', reason: cancelled ? 'cancelled' : 'unavailable' });
            if (cancelled) return;
            try {
              await copyText(link);
              analytics('referral_share_completed', { method: 'copy' });
              showToast(uiText('referral.shareUnavailableCopied', 'Sharing is unavailable right now. Invitation link copied instead.'));
            } catch (_) {
              analytics('referral_share_failed', { method: 'copy', reason: 'unavailable' });
              showToast(uiText('referral.copyFailed', 'Could not copy the link'));
            }
          },
        );
      });
      actions.appendChild(shareButton);
      primaryAction = shareButton;
    } else {
      const copyButton = make('button', {
        className: 'referral-copy',
        id: 'referral-copy',
        text: uiText('referral.copy', 'Copy link'),
        type: 'button',
      });
      copyButton.addEventListener('click', async () => {
        try {
          await copyText(link);
          analytics('referral_share_completed', { method: 'copy' });
          showToast(uiText('referral.copied', 'Invitation link copied'));
        } catch (_) {
          analytics('referral_share_failed', { method: 'copy', reason: 'unavailable' });
          showToast(uiText('referral.copyFailed', 'Could not copy the link'));
        }
      });
      actions.appendChild(copyButton);
      primaryAction = copyButton;
    }

    /* ONE line, at the end, small. It was four separate paragraphs -- the
       terms, the Exchange restriction, the cap note, and the progress line --
       stacked above the thing the player came to press. */
    const footnote = make('p', {
      className: 'referral-dialog-footnote',
      id: 'referral-dialog-terms',
      text: uiText(
        'referral.fineprint',
        'You each get {packs} card packs and Moonlight once your friend finishes {matches} matches within {days} days. First {cap} invitations. The packs are yours to keep, and the Moonlight stays until you use it.',
        { ...displayRewardTerms(terms), cap: terms.cap },
      ),
    });

    // Two wrappers so the desktop column can be one block. On a landscape phone
    // page box they go `display: contents` and the five rows become grid items
    // directly, which is what lets the counter and the button share a line.
    const summary = make('div', { className: 'referral-dialog-summary' });
    summary.append(eyebrow, title, stats);
    const invite = make('div', { className: 'referral-dialog-invite' });
    invite.append(actions, footnote);
    /* Summary and actions share ONE cell beside the ticket. As two grid rows
       spanned by a tall object they were forced apart to match its height --
       78px of dead band between the offer and the button. */
    const body = make('div', { className: 'referral-dialog-body' });
    body.append(summary, invite);
    card.append(closeButton, object, body);
    overlay.appendChild(card);
    overlay.addEventListener('click', event => {
      if (event.target === overlay) close();
    });
    return {
      overlay,
      ticketArt,
      initialFocus: primaryAction,
    };
  }

  function renderLocalizedPresentation() {
    renderBannerStatus();
    if (!dialog?.isConnected || !program) return;

    const focusedId = dialog.contains(document.activeElement)
      ? document.activeElement.id
      : '';
    const rebuilt = buildProgramDialog(program);
    dialog.replaceWith(rebuilt.overlay);
    dialog = rebuilt.overlay;

    const restoredFocus = focusedId ? document.getElementById(focusedId) : null;
    if (restoredFocus && dialog.contains(restoredFocus)) restoredFocus.focus();
    else rebuilt.initialFocus?.focus();
  }

  /**
   * @param {{ returnFocus?: HTMLElement|null }} [options] the control focus
   *   returns to on close. The banner's button names itself: the click guard
   *   disables it while the open is in flight, and a disabled control is not
   *   `document.activeElement` in every engine.
   */
  function open({ returnFocus = null } = {}) {
    if (dialog) return Promise.resolve(false);
    if (openInFlight) return openInFlight;
    const getOrCreate = MoonfallCloud?.referrals?.getOrCreateCode;
    if (typeof getOrCreate !== 'function') {
      showToast(uiText('referral.unavailable', 'Invitations are temporarily unavailable'));
      return Promise.resolve(false);
    }
    if (!claimLobbySurface()) return Promise.resolve(false);
    analytics('referral_program_viewed');
    previousFocus = returnFocus instanceof HTMLElement
      ? returnFocus
      : (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const generation = ++openGeneration;
    /* Best-effort and memoized: a hover already started this, a cold press
       starts it now, in parallel with the round-trip below. */
    warmInviteTicketArt();
    let remoteRequest;
    try {
      remoteRequest = Promise.all([
        getOrCreate.call(MoonfallCloud.referrals),
        refreshInFlight || (statusLoaded ? Promise.resolve() : refresh()),
      ]);
    } catch (error) {
      remoteRequest = Promise.reject(error);
    }
    const session = createOpenSession(remoteRequest);
    pendingOpen = session;
    let request;
    request = (async () => {
      try {
        const outcome = await session.outcome;
        if (pendingOpen !== session || !openSessionIsCurrent(generation)) return false;
        if (outcome.kind === 'timeout') throw new Error('referral_open_timeout');
        if (outcome.kind === 'failed') throw outcome.error;
        if (outcome.kind !== 'loaded') return false;
        const [loaded] = outcome.value;
        if (!loaded?.code || !/^[0-9a-f]{32}$/.test(loaded.code)) {
          throw new Error('invalid_referral_program');
        }
        program = loaded;
        serverTerms = loaded;
        renderBannerStatus();
        if (loaded.created === true) analytics('referral_link_created');
        const built = buildProgramDialog(loaded);
        /* The whole dialog waits on the ticket, not just the ticket: its
           entrance is the ceremony and the ticket is the centrepiece, so the
           card must not fade in around a hole. Nothing is on screen yet -- the
           button is what shows the wait -- so there is no visible element to
           re-hide, and a session closed or superseded meanwhile (a match
           starting, a second press) is dropped here rather than appended
           over the wrong surface. */
        await Promise.race([
          whenInviteTicketWhole(built.ticketArt, { capMs: TICKET_WHOLE_CAP_MS }),
          session.cancelled,
        ]);
        if (pendingOpen !== session || !openSessionIsCurrent(generation)) return false;
        dialog = built.overlay;
        const app = document.getElementById('app');
        appWasInert = Boolean(app?.inert);
        if (app) app.inert = true;
        document.body.appendChild(dialog);
        document.addEventListener('keydown', onDialogKeyDown, true);
        built.initialFocus?.focus();
        return true;
      } catch (error) {
        if (!openSessionIsCurrent(generation)) return false;
        console.warn('[Referrals] Failed to open referral program:', error?.message || error);
        close();
        showToast(uiText('referral.unavailable', 'Invitations are temporarily unavailable'));
        return false;
      } finally {
        if (pendingOpen === session) pendingOpen = null;
        if (openInFlight === request) openInFlight = null;
      }
    })();
    openInFlight = request;
    return openInFlight;
  }

  function lobbyIsVisible() {
    const lobby = document.getElementById('lobby-view');
    if (!lobby || lobby.hidden) return false;
    const style = window.getComputedStyle(lobby);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function consumeAndShowRewards() {
    if (!lobbyIsVisible() || document.getElementById('starter-reward-overlay')) {
      return Promise.resolve(false);
    }
    if (consumeInFlight) return consumeInFlight;
    const consume = MoonfallCloud?.referrals?.consumeRewardAnnouncements;
    const show = StarterReward?.show;
    if (typeof consume !== 'function' || typeof show !== 'function') return Promise.resolve(false);

    consumeInFlight = Promise.resolve()
      .then(() => consume.call(MoonfallCloud.referrals))
      .then(async announcements => {
        /* WHAT A QUALIFIED REFERRAL PAYS: card packs and Moonlight. A row is
           worth announcing if ANY of the three figures is positive — `amount`
           only for rows written before the reward changed shape, which are
           still owed their ceremony rather than a silent delete. Validating on
           `amount` alone is what silenced every new payout. */
        const figure = (value) => (Number.isSafeInteger(value) && value > 0 ? value : 0);
        const valid = Array.isArray(announcements)
          ? announcements.filter(item => (
            item
            && ['referral_inviter', 'referral_invitee'].includes(item.kind)
            && (figure(item.packs) || figure(item.moonlightUsdMicros) || figure(item.amount))
          ))
          : [];
        if (!valid.length) return false;
        const totals = valid.reduce((sum, item) => ({
          packs: sum.packs + figure(item.packs),
          moonlightUsdMicros: sum.moonlightUsdMicros + figure(item.moonlightUsdMicros),
          amount: sum.amount + figure(item.amount),
        }), { packs: 0, moonlightUsdMicros: 0, amount: 0 });
        /* MOONLIGHT MUST ANNOUNCE ITSELF, and BEFORE the legacy branch below —
           `totals.amount` is zero for every referral written since the reward
           became packs + Moonlight, so anything nested under it never runs.
           That is why the owner saw "no visible reward": the grant committed,
           the chip beside it kept the stale figure, and only a reload fixed it.
           The chip re-reads its own status from the server; we do not push a
           number at it. */
        let moonlightPercent = null;
        if (totals.moonlightUsdMicros > 0) {
          if (typeof window !== 'undefined') {
            try {
              window.dispatchEvent(new CustomEvent('moonfall:moonlight-changed'));
            } catch (_) { /* presentation only: the grant is already durable */ }
          }
          /* READ THE MOON BACK so the ceremony can say how full it now is. The
             server is the only thing that knows the cycle size, and this is
             AFTER the grant committed, so the figure is the post-payout one.
             A failed read leaves it null and the line falls back to its plain
             sentence — never to 0%, which would read as "you got nothing". */
          try {
            const status = await MoonfallCloud?.users?.getMoonlightStatus?.();
            const percent = Number(status?.percent);
            if (status?.state === 'known' && Number.isFinite(percent)) {
              moonlightPercent = percent;
            }
          } catch (_) { /* the reward stands whether or not the meter answers */ }
        }
        /* The balance line only moves for a legacy Stardust payout; packs are
           counted on the collection screen, which reads its own state. */
        if (totals.amount > 0) {
          try {
            const profile = await MoonfallCloud?.users?.getProfile?.();
            const balance = profile?.stardust;
            /* Through the one chip painter (bare number; the glyph is CSS). */
            paintStardustChip(document.getElementById('user-stardust'), balance);
          } catch (_) { /* the durable reward remains valid even if balance refresh fails */ }
        }
        return show.call(StarterReward, { ...totals, kind: 'referral', moonlightPercent });
      })
      .finally(() => { consumeInFlight = null; });
    return consumeInFlight;
  }

  async function consumeLobbyRewards() {
    const consumeStarter = StarterReward?.consumeAndShow;
    if (typeof consumeStarter === 'function') {
      try {
        const starterShown = await consumeStarter.call(StarterReward);
        if (starterShown) return true;
      } catch (error) {
        console.warn('[Referrals] Failed to check starter reward:', error?.message || error);
      }
    }
    return await consumeAndShowRewards();
  }

  function init() {
    if (initialized) return;
    initialized = true;
    createBanner();
    window.addEventListener('resize', syncBannerPlacement, { passive: true });
    window.addEventListener('orientationchange', syncBannerPlacement, { passive: true });
    window.addEventListener('moonfall:viewport-change', syncBannerPlacement);
    // Parameterized copy cannot be refreshed by the static data-i18n pass:
    // render it again with the authoritative reward terms on every locale
    // transition instead of leaving the banner half-translated.
    window.addEventListener('moonfall:i18nready', renderLocalizedPresentation);
    window.addEventListener('moonfall:languagechange', () => {
      renderLocalizedPresentation();
      try { relocalizeInviteTicket(document.getElementById('referral-dialog')); } catch (_) { /* decoration */ }
    });
    void refresh();
  }

  return {
    close,
    consumeAndShowRewards,
    consumeLobbyRewards,
    init,
    open,
    refresh,
  };
})();

export { Referrals };
